/**
 * Efectos de stock al confirmar una venta (POS): disponibilidad, descuento agregado, FEFO y colas de sync de lotes.
 */
import { formatQty, normalizeUnitOfMeasure } from '@shared/quantity';
import { getCatalogRamSnapshotCache } from '../../../../catalog/infra/catalogRamSnapshotCache';
import { StockInsufficientError } from '../../domain/stockErrors';
import { getAvailability } from './getAvailability';
import { findStockRow } from './stockRowLookup';

export type SaleStockItemInput = {
  productId: string;
  variantId?: string | null;
  quantity: number;
  name: string;
  sku?: string;
  affectsStock?: boolean;
  options?: Array<{
    choiceId: string;
    choiceName: string;
    qty: number;
    linkedProductId?: string | null;
  }> | null;
};

const shouldAffectStock = (item: SaleStockItemInput) => item.affectsStock !== false;

/** Web ram-only: products SQLite está vacío; el UoM vive en el snapshot de catálogo. */
async function resolveProductUnitOfMeasure(productId: string): Promise<string> {
  const cached = getCatalogRamSnapshotCache()?.products.find((p) => p.id === productId);
        return normalizeUnitOfMeasure(cached?.unit_of_measure);
}

export async function ensureStockAvailabilityForSale(
  branchId: string,
  items: SaleStockItemInput[],
): Promise<void> {
  const trackedItems = items.filter(shouldAffectStock);
  for (const item of trackedItems) {
    const { quantity, sellable, expiredUnits } = await getAvailability(
      branchId,
      item.productId,
      item.variantId,
    );
    if (quantity === 0 && sellable === 0) {
      const stockRow = await findStockRow(item.productId, branchId, item.variantId);
      if (!stockRow) {
        throw new StockInsufficientError(`No hay stock cargado para ${item.name} en esta sucursal.`);
      }
    }
    if (sellable < item.quantity) {
      const uom = await resolveProductUnitOfMeasure(item.productId);
      if (expiredUnits > 0 && quantity >= item.quantity) {
        throw new StockInsufficientError(
          `${item.name}: hay ${formatQty(quantity, uom)} en total pero ${formatQty(expiredUnits, uom)} están vencidas. Disponible para venta: ${formatQty(sellable, uom)}.`,
        );
      }
      throw new StockInsufficientError(
        `Stock insuficiente para ${item.name}. Disponible: ${formatQty(sellable, uom)}.`,
      );
    }
    // Linked product stock validation from menu options
    if (item.options?.length) {
      for (const opt of item.options) {
        if (!opt.linkedProductId) continue;
        const linkedQty = (opt.qty ?? 1) * item.quantity;
        const { quantity: lQty, sellable: lSellable, expiredUnits: lExpired } =
          await getAvailability(branchId, opt.linkedProductId, null);
        if (lQty === 0 && lSellable === 0) {
          const lStockRow = await findStockRow(opt.linkedProductId, branchId, null);
          if (!lStockRow) {
            throw new StockInsufficientError(`No hay stock cargado para ${opt.choiceName || 'insumo'} en esta sucursal.`);
          }
        }
        if (lSellable < linkedQty) {
          const lUom = await resolveProductUnitOfMeasure(opt.linkedProductId);
          const lName = opt.choiceName || 'insumo';
          if (lExpired > 0 && lQty >= linkedQty) {
            throw new StockInsufficientError(
              `${lName}: hay ${formatQty(lQty, lUom)} en total pero ${formatQty(lExpired, lUom)} están vencidas. Disponible para venta: ${formatQty(lSellable, lUom)}.`,
            );
          }
          throw new StockInsufficientError(
            `Stock insuficiente para ${lName}. Disponible: ${formatQty(lSellable, lUom)}.`,
          );
        }
      }
    }
  }
}
